import type { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON, RegistrationResponseJSON, AuthenticationResponseJSON } from '@simplewebauthn/browser';
import { createApiClient, createRawApiClient } from './client';
import type { Factor, TotpEnrollment } from './types';

/**
 * MFA factor management + step-up (Stream 4 Phase 1). Thin typed wrappers over
 * the façade's `/me/factors/*` and `/me/stepup/*` passthrough routes
 * (services/steward-app-api/src/handlers/factors.ts + stepup.ts), which relay
 * appcore's response bodies verbatim — still wrapped in `{ data: ... }` — so
 * every function here unwraps `.data` once, in one place, rather than at each
 * call site.
 *
 * Deliberately plain `fetch`-backed calls (via {@link createApiClient}), not
 * React Query: enrollment and step-up are one-shot ceremonies driven by user
 * gesture (a button click, a submitted code), not cacheable reads.
 */

// ── Factor enrollment / management ──

/**
 * Shared React Query key for the caller's factor list — used by both
 * EnrollmentGate (the gate's own read) and Settings → Sign-in keys (the
 * management view), so that adding/removing a factor in Settings invalidates
 * the SAME cache entry the gate reads, without either file re-deriving the
 * key as a separate literal that could silently drift out of sync.
 */
export const FACTORS_QUERY_KEY = ['me', 'factors'] as const;

export function listFactors(token: string): Promise<Factor[]> {
  return createApiClient(token).get<{ data: { factors: Factor[] } }>('/me/factors').then(r => r.data.factors);
}

export function webauthnRegisterOptions(token: string): Promise<PublicKeyCredentialCreationOptionsJSON> {
  return createApiClient(token)
    .post<{ data: { options: PublicKeyCredentialCreationOptionsJSON } }>('/me/factors/webauthn/register/options', {})
    .then(r => r.data.options);
}

/**
 * What appcore reports about the step-up it minted as part of a successful
 * enrollment ceremony (appcore docs/29 F1, decisions log §50): registering a
 * passkey or activating a TOTP is itself a user-verifying proof, so appcore mints
 * the session's step-up in the same transaction as the factor write rather than
 * making the user run a second ceremony seconds later.
 *
 * BOTH fields are optional on purpose. steward-app and appcore deploy
 * independently, so a SPA running against an older appcore sees `undefined` here
 * and must fall back to running the ceremony explicitly. Treat only an explicit
 * `stepUpMinted === true` as "a live step-up exists"; never infer it from absence.
 */
export interface EnrollmentStepUp {
  /** True when appcore wrote a `STEPUP#<origin_jti>` row for this session. */
  stepUpMinted?: boolean;
  /** Present only alongside `stepUpMinted: true`; mirrors the /me/stepup/* vocabulary. */
  assuranceLevel?: 'stepup';
}

/**
 * The operator recovery code (appcore docs/29 F29). Send it ONLY when the server has
 * asked for it, by answering a `400 RECOVERY_CODE_REQUIRED`: there is deliberately no
 * endpoint that reports whether an account is under a recovery hold, so the error code
 * is the whole signal.
 *
 * Sent as typed, unnormalized. appcore folds separators, case, and Crockford's I/L/O
 * aliases at compare time, and a second normalizer here could only drift away from the
 * one that decides the comparison.
 */
export interface RecoveryCodeInput {
  recoveryCode?: string;
}

export function webauthnRegisterVerify(
  token: string,
  input: { response: RegistrationResponseJSON; label?: string } & RecoveryCodeInput,
): Promise<{ factorId: string; factorType: 'webauthn' } & EnrollmentStepUp> {
  return createApiClient(token)
    .post<{ data: { factorId: string; factorType: 'webauthn' } & EnrollmentStepUp }>('/me/factors/webauthn/register/verify', input)
    .then(r => r.data);
}

/** Mints a new TOTP secret + otpauth URI. appcore returns it ONCE (Cache-Control:
 *  no-store, mirrored by the façade) — the caller must render it immediately and
 *  never persist it past the enrollment screen. */
export function totpEnroll(token: string, input: { label?: string } & RecoveryCodeInput = {}): Promise<TotpEnrollment> {
  return createApiClient(token).post<{ data: TotpEnrollment }>('/me/factors/totp/enroll', input).then(r => r.data);
}

/**
 * Activation is where appcore CONSUMES a recovery hold, so `recoveryCode` belongs here
 * as well as on {@link totpEnroll} (docs/29 F29). Enrollment only checks it.
 */
export function totpActivate(
  token: string,
  input: { factorId: string; code: string } & RecoveryCodeInput,
): Promise<{ factorId: string; factorType: 'totp'; status: 'active' } & EnrollmentStepUp> {
  return createApiClient(token)
    .post<{ data: { factorId: string; factorType: 'totp'; status: 'active' } & EnrollmentStepUp }>('/me/factors/totp/activate', input)
    .then(r => r.data);
}

export function deleteFactor(token: string, factorId: string): Promise<{ deleted: true }> {
  return createApiClient(token)
    .delete<{ data: { deleted: true } }>(`/me/factors/${encodeURIComponent(factorId)}`)
    .then(r => r.data);
}

// ── Step-up ceremony ──
// Four importing files, no others (pinned by src/api/stepUpCallers.test.ts, which reads the
// import statements; the list was read from the source on 2026-10-07):
//   - the step-up controller (src/mfa/StepUpProvider.tsx), which a gated call's 403
//     triggers automatically via the interceptor in client.ts;
//   - the snapshot form's own inline prompt (src/intake/IntakeSecondCheckPrompt.tsx), which
//     asks the same three questions inside the form's layout because the controller's
//     dialog is a fixed full-window layer with links into the app and a workspace, which
//     the form must not draw;
//   - the onboarding flow's own verify step (src/onboarding/OnboardingFlow.tsx); and
//   - the login-link modal's verify step (src/pages/settings/LinkLoginModal.tsx).
// A page that needs a step-up for a gated call does not call these: it makes the gated
// call, and the interceptor in client.ts raises the ceremony. These deliberately
// use `createRawApiClient`, NOT `createApiClient`: they exist to SATISFY a
// step-up gate, so they must never themselves be routed back through the
// interceptor (see createRawApiClient's doc comment for why that would deadlock).

export function stepUpWebauthnOptions(token: string): Promise<PublicKeyCredentialRequestOptionsJSON> {
  return createRawApiClient(token)
    .post<{ data: { options: PublicKeyCredentialRequestOptionsJSON } }>('/me/stepup/webauthn/options', {})
    .then(r => r.data.options);
}

export function stepUpWebauthnVerify(
  token: string,
  input: { response: AuthenticationResponseJSON },
): Promise<{ assuranceLevel: 'stepup'; factorType: 'webauthn' }> {
  return createRawApiClient(token)
    .post<{ data: { assuranceLevel: 'stepup'; factorType: 'webauthn' } }>('/me/stepup/webauthn/verify', input)
    .then(r => r.data);
}

export function stepUpTotp(token: string, input: { code: string }): Promise<{ assuranceLevel: 'stepup'; factorType: 'totp' }> {
  return createRawApiClient(token)
    .post<{ data: { assuranceLevel: 'stepup'; factorType: 'totp' } }>('/me/stepup/totp', input)
    .then(r => r.data);
}
